/**
 * Bulk Approve Validation Utility
 * 
 * Provides frontend validation for bulk approval of GLs/SLs based on
 * Reporting Unit (RU) configuration and GL-level threshold flags.
 * This validation prevents unnecessary API calls when selected items
 * don't meet bulk approval criteria.
 */

/**
 * Error reasons for bulk approval validation failures
 */
export const BULK_APPROVE_VALIDATION_MESSAGES = {
  NOT_AWAITING_LOCAL_APPROVAL: "GL is not in 'Awaiting Commentary approval' status",
  NOT_AWAITING_REGIONAL_APPROVAL: "GL is not in 'Awaiting Regional approval' status",
  LOCAL_STAGE_NOT_COMPLETED: "Local approval not completed for this GL",
  CROSSED_CONTROL_THRESHOLD: "GL crossed Control threshold - must be approved individually",
  CROSSED_MANAGEMENT_THRESHOLD: "GL crossed Management threshold - must be approved individually",
  GENERIC_ERROR: "The selected GLs are either not in the status of 'Awaiting Commentary approval' or Bulk Approval has been disabled",
};

/**
 * Valid local stage values that indicate completed local approval
 */
const COMPLETED_LOCAL_STAGES = [2, 3, 4];

/**
 * Stage value for awaiting approval
 */
const AWAITING_APPROVAL_STAGE = 1;

/**
 * Validates a single GL for bulk approval eligibility
 * Handles both local and regional approver validation in a unified function.
 * 
 * @param {Object} gl - GL object with stage and threshold flags
 * @param {Object} ruConfig - Reporting Unit configuration
 * @param {boolean} ruConfig.bulkapprove_control - Whether bulk approve is allowed for control threshold
 * @param {boolean} ruConfig.bulkapprove_management - Whether bulk approve is allowed for management threshold
 * @param {boolean} isRegionalApprover - Whether the user is a regional approver
 * @returns {Object} - { valid: boolean, reason: string|null }
 */
function validateGLForBulkApprove(gl, ruConfig, isRegionalApprover) {
  const { bulkapprove_control = false, bulkapprove_management = false } = ruConfig || {};
  const stageLocal = gl.stage_local ?? gl.__stage;
  
  if (isRegionalApprover) {
    // Regional: check stage_local completed and stage_regional awaiting
    if (!COMPLETED_LOCAL_STAGES.includes(stageLocal)) {
      return { valid: false, reason: BULK_APPROVE_VALIDATION_MESSAGES.LOCAL_STAGE_NOT_COMPLETED };
    }
    
    const stageRegional = gl.stage_regional ?? gl.__stageregional;
    if (stageRegional !== AWAITING_APPROVAL_STAGE) {
      return { valid: false, reason: BULK_APPROVE_VALIDATION_MESSAGES.NOT_AWAITING_REGIONAL_APPROVAL };
    }
    
    // Check regional threshold flags
    const crossesControl = Boolean(gl.var1_cross_control_regional || gl.var2_cross_control_regional);
    const crossesManagement = Boolean(gl.var1_cross_management_regional || gl.var2_cross_management_regional);
    
    if (!bulkapprove_control && crossesControl) {
      return { valid: false, reason: BULK_APPROVE_VALIDATION_MESSAGES.CROSSED_CONTROL_THRESHOLD };
    }
    if (!bulkapprove_management && crossesManagement) {
      return { valid: false, reason: BULK_APPROVE_VALIDATION_MESSAGES.CROSSED_MANAGEMENT_THRESHOLD };
    }
  } else {
    // Local: check stage_local awaiting
    if (stageLocal !== AWAITING_APPROVAL_STAGE) {
      return { valid: false, reason: BULK_APPROVE_VALIDATION_MESSAGES.NOT_AWAITING_LOCAL_APPROVAL };
    }
    
    // Check local threshold flags
    const crossesControl = Boolean(gl.var1_cross_control || gl.var2_cross_control);
    const crossesManagement = Boolean(gl.var1_cross_management || gl.var2_cross_management);
    
    if (!bulkapprove_control && crossesControl) {
      return { valid: false, reason: BULK_APPROVE_VALIDATION_MESSAGES.CROSSED_CONTROL_THRESHOLD };
    }
    if (!bulkapprove_management && crossesManagement) {
      return { valid: false, reason: BULK_APPROVE_VALIDATION_MESSAGES.CROSSED_MANAGEMENT_THRESHOLD };
    }
  }

  return { valid: true, reason: null };
}

/**
 * Validates multiple GLs for bulk approval eligibility
 * 
 * @param {Array} selectedGLs - Array of selected GL objects
 * @param {Object} reportingUnit - Reporting Unit object with bulkapprove_control and bulkapprove_management flags
 * @param {string} userRole - 'approver' for local approver, 'regionalapprover' for regional approver
 * @returns {Object} - { valid: boolean, invalidGLs: Array<{gl, reason}>, errorMessage: string, validGLs: Array }
 */
export function validateBulkApprove(selectedGLs, reportingUnit, userRole) {
  if (!selectedGLs || selectedGLs.length === 0) {
    return {
      valid: false,
      invalidGLs: [],
      validGLs: [],
      errorMessage: 'No GLs selected for bulk approval',
    };
  }

  const ruConfig = {
    bulkapprove_control: Boolean(reportingUnit?.bulkapprove_control),
    bulkapprove_management: Boolean(reportingUnit?.bulkapprove_management),
  };

  const isRegionalApprover = userRole === 'regionalapprover';
  const invalidGLs = [];
  const validGLs = [];

  for (const gl of selectedGLs) {
    const result = validateGLForBulkApprove(gl, ruConfig, isRegionalApprover);
    if (result.valid) {
      validGLs.push(gl);
    } else {
      invalidGLs.push({
        gl,
        glCode: gl.glcode_detail?.code || gl.glcode || gl.__code || gl._id,
        glName: gl.glcode_detail?.name || gl.name || '',
        reason: result.reason,
      });
    }
  }

  const isValid = invalidGLs.length === 0;

  return {
    valid: isValid,
    invalidGLs,
    validGLs,
    errorMessage: isValid ? '' : BULK_APPROVE_VALIDATION_MESSAGES.GENERIC_ERROR,
    summary: {
      total: selectedGLs.length,
      valid: validGLs.length,
      invalid: invalidGLs.length,
    },
  };
}

export default {
  validateBulkApprove,
  BULK_APPROVE_VALIDATION_MESSAGES,
};
